import { useEmbedContext } from '@thoughtspot/blink-context';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { translate as t } from '@thoughtspot/i18n';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { isAdminModernExperienceEnabled, isCustomBannersEnabled, isHomepageV4Enabled, isManualTranslationConfigEnabled, isOrgUiEnabled, } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useMemo, useState, } from 'react';
import { BasePageContext } from "../../contexts/basepage-context";
import { AdminContextProvider, useAdminContext } from "../../contexts/context";
import { BannerLevel, HomepageModuleConfigLevel, } from "../../services/generated/graphql-types";
import { ErrorBoundary } from '../error-boundary/error-boundary.container';
import { Header } from '../system/common';
import { Mode } from '../system/common/constants';
import { AutoTranslationToggleContainer } from './auto-translation-toggle';
import { ConfigAnnouncementContainer } from './config-announcement';
import styles from './home-page-discovery.module.scss';
import { HomepageExperienceToggleContainer } from './homepage-experience-toggle';
import { LiveboardCacheConfigContainer } from './liveboard-cache-config';
import { ManualTranslationToggleContainer } from './manual-translation-toggle';
const TOAST_MESSAGES = {
    publishSuccess: t('admin.announcementPage.publishSuccess'),
    deleteSuccess: t('admin.announcementPage.deleteSuccess'),
};
const useSectionState = (initialLoading = false) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(initialLoading);
    const [isSaving, setIsSaving] = useState(false);
    const handleEditingChange = useCallback((editing) => {
        setIsEditing(editing);
    }, []);
    const handleLoadingChange = useCallback((loading) => {
        setIsLoading(prev => (prev !== loading ? loading : prev));
    }, []);
    const handleSavingChange = useCallback((saving) => {
        setIsSaving(saving);
    }, []);
    return {
        isEditing,
        isLoading,
        isSaving,
        handleEditingChange,
        handleLoadingChange,
        handleSavingChange,
    };
};
export const HomePageDiscovery = ({ orgViewModeType, }) => {
    const { homeModuleCustomizationContainer } = BasePageContext();
    const { ...adminContext } = useAdminContext();
    const isManualTranslationEnabled = isManualTranslationConfigEnabled();
    const autoTranslation = useSectionState(true);
    const manualTranslation = useSectionState(isManualTranslationEnabled);
    const liveboardCache = useSectionState(false);
    const homepageExperience = useSectionState(false);
    const [isV4Active, setIsV4Active] = useState(isHomepageV4Enabled());
    const [isCustomizeHomepageLoading, setIsCustomizeHomepageLoading,] = useState(!isHomepageV4Enabled());
    const isPageLoading = isCustomizeHomepageLoading ||
        autoTranslation.isLoading ||
        manualTranslation.isLoading ||
        autoTranslation.isSaving ||
        manualTranslation.isSaving;
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [showRefreshAction, setShowRefreshAction] = useState(true);
    const embedContext = useEmbedContext();
    const isAnyEditing = autoTranslation.isEditing ||
        manualTranslation.isEditing ||
        liveboardCache.isEditing ||
        homepageExperience.isEditing;
    const getBannerLevel = () => {
        if (!isOrgUiEnabled()) {
            return BannerLevel.Cluster;
        }
        return orgViewModeType === 'all-org'
            ? BannerLevel.Cluster
            : BannerLevel.Org;
    };
    const getUserType = () => {
        if (!isOrgUiEnabled()) {
            return HomepageModuleConfigLevel.Cluster;
        }
        return orgViewModeType === 'all-org'
            ? HomepageModuleConfigLevel.Cluster
            : HomepageModuleConfigLevel.Org;
    };
    const handleCustomizeHomepageLoadingChange = useCallback((loading) => {
        setIsCustomizeHomepageLoading(prevLoading => {
            if (prevLoading !== loading) {
                return loading;
            }
            return prevLoading;
        });
    }, []);
    const showToast = useCallback((message, withRefreshAction = true) => {
        setToastMessage(message);
        setShowRefreshAction(withRefreshAction);
        setIsToastVisible(true);
    }, []);
    useEffect(() => {
        const toastType = localStorage.getItem('HomepageAnnouncementToast');
        if (toastType) {
            localStorage.removeItem('HomepageAnnouncementToast');
            const message = TOAST_MESSAGES[toastType];
            if (message) {
                showToast(message, false);
            }
        }
    }, []);
    const primaryActionForToast = useMemo(() => ({
        label: t('admin.refresh'),
        onClick: () => {
            window.location.reload();
        },
    }), []);
    const configSections = [
        {
            key: 'autoTranslation',
            isVisible: !manualTranslation.isEditing &&
                !liveboardCache.isEditing &&
                !homepageExperience.isEditing,
            content: (React.createElement(AutoTranslationToggleContainer, { orgViewModeType: orgViewModeType, onEditingChange: autoTranslation.handleEditingChange, onLoadingChange: autoTranslation.handleLoadingChange, onSavingChange: autoTranslation.handleSavingChange, onShowToast: showToast })),
        },
        {
            key: 'manualTranslation',
            isVisible: isManualTranslationEnabled &&
                !autoTranslation.isEditing &&
                !liveboardCache.isEditing &&
                !homepageExperience.isEditing,
            content: (React.createElement(ManualTranslationToggleContainer, { orgViewModeType: orgViewModeType, onEditingChange: manualTranslation.handleEditingChange, onLoadingChange: manualTranslation.handleLoadingChange, onSavingChange: manualTranslation.handleSavingChange, onShowToast: showToast })),
        },
        {
            key: 'liveboardCache',
            isVisible: !autoTranslation.isEditing &&
                !manualTranslation.isEditing &&
                !homepageExperience.isEditing,
            content: (React.createElement(LiveboardCacheConfigContainer, { orgViewModeType: orgViewModeType, onEditingChange: liveboardCache.handleEditingChange, onShowToast: showToast })),
        },
    ];
    const renderConfigSection = (section) => (React.createElement(React.Fragment, { key: section.key }, section.isVisible && (React.createElement(React.Fragment, null,
        !isAnyEditing && React.createElement(Divider, null),
        React.createElement(Vertical, null, section.content)))));
    return (React.createElement(React.Fragment, null,
        isPageLoading && (React.createElement(LoadingIndicator.Global, { hideLoader: false, className: styles.fullScreenLoader },
            React.createElement(React.Fragment, null))),
        React.createElement(Vertical, { className: isPageLoading
                ? styles.homePageDiscoveryViewContainerLoading
                : styles.homePageDiscoveryViewContainer },
            !isAnyEditing && !isAdminModernExperienceEnabled() && (React.createElement(Header, { title: t('adminSection.homePageAppExperience.title'), titleSize: "large", isFullWidth: true, mode: Mode.VIEW })),
            React.createElement(Vertical, { className: styles.homePageDiscoveryContent },
                !isAnyEditing &&
                    isCustomBannersEnabled() &&
                    !embedContext.isEmbedded && (React.createElement(React.Fragment, null,
                    React.createElement(Vertical, null,
                        React.createElement(AdminContextProvider, { ...adminContext },
                            React.createElement(ErrorBoundary, null,
                                React.createElement(ConfigAnnouncementContainer, { bannerLevel: getBannerLevel(), onShowToast: showToast })))))),
                React.createElement(AdminContextProvider, { ...adminContext },
                    React.createElement(ErrorBoundary, null,
                        !autoTranslation.isEditing &&
                            !manualTranslation.isEditing &&
                            !liveboardCache.isEditing &&
                            (!isOrgUiEnabled() ||
                                orgViewModeType === 'all-org') && (React.createElement(React.Fragment, null,
                            !isAnyEditing && React.createElement(Divider, null),
                            React.createElement(Vertical, null,
                                React.createElement(HomepageExperienceToggleContainer, { orgViewModeType: orgViewModeType, onEditingChange: homepageExperience.handleEditingChange, onLoadingChange: homepageExperience.handleLoadingChange, onSavingChange: homepageExperience.handleSavingChange, onShowToast: showToast, onVersionChange: setIsV4Active })))),
                        !isAnyEditing && !isV4Active && (React.createElement(React.Fragment, null,
                            React.createElement(Divider, null),
                            homeModuleCustomizationContainer({
                                userType: getUserType(),
                                onLoadingChange: handleCustomizeHomepageLoadingChange,
                                onShowToast: showToast,
                            }))),
                        configSections.map(renderConfigSection))))),
        React.createElement(GlobalAlert.Toast, { isVisible: isToastVisible, message: toastMessage, primaryAction: showRefreshAction ? primaryActionForToast : undefined, onClose: () => {
                setIsToastVisible(false);
                setToastMessage('');
                setShowRefreshAction(true);
            }, autoHide: true, showCheckmarkIcon: true })));
};
//# sourceMappingURL=home-page-discovery.container.js.map